import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Plus, Trash2, Loader2, CalendarRange, Briefcase } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEEKDAYS_SV = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'];

function startOfWeek(d) {
  const day = (d.getDay() + 6) % 7;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - day);
}
function fmtDateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
// ISO week parity (1=odd, 0=even)
function isoWeekNumber(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
}

export default function WeekTemplatesModal({ onClose, onApplied }) {
  const [templates, setTemplates] = useState([]);
  const [workplaces, setWorkplaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | template object
  const [applying, setApplying] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tpls, wps] = await Promise.all([
        axios.get(`${API}/work-templates`, { withCredentials: true }).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/workplaces`, { withCredentials: true }).catch(() => ({ data: { items: [] } })),
      ]);
      setTemplates(tpls.data?.items || []);
      setWorkplaces(wps.data?.items || []);
    } catch { toast.error('Kunde inte ladda mallar'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm('Ta bort mallen?')) return;
    try {
      await axios.delete(`${API}/work-templates/${id}`, { withCredentials: true });
      toast.success('Borttagen');
      load();
    } catch { toast.error('Kunde inte ta bort'); }
  };

  const apply = async (tpl, weekKind) => {
    // weekKind: "this" | "next" | "odd" | "even"
    setApplying(tpl.id + '-' + weekKind);
    try {
      const today = new Date();
      const thisMonday = startOfWeek(today);
      let mondays = [];
      if (weekKind === 'this') {
        mondays = [thisMonday];
      } else if (weekKind === 'next') {
        const m = new Date(thisMonday); m.setDate(m.getDate() + 7);
        mondays = [m];
      } else {
        // Apply to all matching odd/even weeks for the next 8 weeks
        for (let i = 0; i < 8; i++) {
          const m = new Date(thisMonday); m.setDate(m.getDate() + 7 * i);
          const wn = isoWeekNumber(m);
          const isOdd = wn % 2 === 1;
          if ((weekKind === 'odd' && isOdd) || (weekKind === 'even' && !isOdd)) mondays.push(m);
        }
      }
      let totalCreated = 0;
      let totalSkipped = 0;
      for (const m of mondays) {
        const res = await axios.post(`${API}/work-templates/${tpl.id}/apply`, { week_start: fmtDateKey(m) }, { withCredentials: true });
        totalCreated += res.data?.created || 0;
        totalSkipped += (res.data?.skipped || []).length;
      }
      toast.success(`✨ ${totalCreated} pass tillagda${totalSkipped ? ` (${totalSkipped} hoppades över)` : ''}`);
      onApplied?.();
    } catch { toast.error('Kunde inte applicera mallen'); }
    finally { setApplying(null); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-2 sm:p-4" onClick={onClose} data-testid="week-templates-backdrop">
      <div className="neo-card bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="week-templates-modal">
        <header className="sticky top-0 z-10 bg-[#A2D2FF] p-3 flex items-center justify-between border-b-2 border-black/10">
          <h3 className="font-['Fredoka'] font-bold text-lg flex items-center gap-2"><CalendarRange className="w-5 h-5" /> Veckomallar (jobbskift)</h3>
          <button onClick={onClose} className="p-1" data-testid="week-templates-close"><X className="w-5 h-5" /></button>
        </header>

        <div className="p-3 space-y-3">
          {loading && <div className="text-center py-6"><Loader2 className="w-6 h-6 animate-spin mx-auto opacity-60" /></div>}

          {!loading && workplaces.length === 0 && (
            <div className="neo-card p-3 bg-[#FFF4D6] text-sm">
              💡 Du behöver först lägga till mottagningar i din "Dagens jobb"-sektion innan du kan skapa veckomallar.
            </div>
          )}

          {!loading && workplaces.length > 0 && !editing && (
            <>
              {templates.length === 0 ? (
                <div className="text-center py-4 opacity-60 text-sm">Inga mallar än. Skapa en för att applicera samma jobbschema varje udda eller jämn vecka.</div>
              ) : (
                <ul className="space-y-2" data-testid="templates-list">
                  {templates.map((tpl) => (
                    <li key={tpl.id} className="neo-card p-3 bg-[#F5EEFF] space-y-2" data-testid={`template-${tpl.id}`}>
                      <div className="flex items-center justify-between">
                        <h4 className="font-bold text-sm">{tpl.name}</h4>
                        <div className="flex gap-1">
                          <button onClick={() => setEditing(tpl)} className="text-[10px] px-2 py-0.5 rounded bg-white border border-black/15" data-testid={`template-edit-${tpl.id}`}>Ändra</button>
                          <button onClick={() => del(tpl.id)} className="text-[10px] px-2 py-0.5 rounded bg-red-100 text-red-700" data-testid={`template-delete-${tpl.id}`}><Trash2 className="w-3 h-3" /></button>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1 text-[10px]">
                        {(tpl.days || []).map((d, i) => {
                          const wp = workplaces.find((w) => w.id === d.workplace_id);
                          return (
                            <span key={i} className="px-1.5 py-0.5 rounded-full bg-white border border-black/15 truncate max-w-[150px]">
                              {WEEKDAYS_SV[d.weekday]} {d.start || ''}–{d.end || ''} · {wp?.name || '?'}
                            </span>
                          );
                        })}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {[
                          { k: 'this', l: 'Denna vecka' },
                          { k: 'next', l: 'Nästa vecka' },
                          { k: 'odd', l: 'Alla udda v. (8v)' },
                          { k: 'even', l: 'Alla jämna v. (8v)' },
                        ].map(({ k, l }) => (
                          <button
                            key={k}
                            onClick={() => apply(tpl, k)}
                            disabled={applying === tpl.id + '-' + k}
                            className="text-[10px] font-bold px-2 py-1 rounded-full bg-[#B9FBC0] border-2 border-[#4DAA4D] hover:shadow-sm transition-all disabled:opacity-50"
                            data-testid={`template-apply-${tpl.id}-${k}`}
                          >
                            {applying === tpl.id + '-' + k ? <Loader2 className="w-3 h-3 animate-spin inline" /> : `▶ ${l}`}
                          </button>
                        ))}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <button onClick={() => setEditing('new')} className="neo-btn px-3 py-2 bg-[#FFE8A3] text-sm font-bold w-full" data-testid="template-new">
                <Plus className="w-4 h-4 inline mr-1" /> Ny mall
              </button>
            </>
          )}

          {!loading && editing && (
            <TemplateEditor
              template={editing === 'new' ? null : editing}
              workplaces={workplaces}
              onSave={() => { setEditing(null); load(); }}
              onCancel={() => setEditing(null)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function TemplateEditor({ template, workplaces, onSave, onCancel }) {
  const [name, setName] = useState(template?.name || '');
  const [days, setDays] = useState(() => {
    const init = {};
    (template?.days || []).forEach((d) => { init[d.weekday] = d; });
    return init;
  });
  const [busy, setBusy] = useState(false);

  const setDay = (weekday, fields) => {
    setDays((prev) => ({ ...prev, [weekday]: { weekday, ...prev[weekday], ...fields } }));
  };
  const removeDay = (weekday) => {
    setDays((prev) => { const n = { ...prev }; delete n[weekday]; return n; });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Namn krävs'); return; }
    const dayList = Object.values(days).filter((d) => d.workplace_id);
    if (!dayList.length) { toast.error('Lägg till minst en arbetsdag'); return; }
    setBusy(true);
    try {
      const payload = { name: name.trim(), days: dayList };
      if (template?.id) {
        await axios.patch(`${API}/work-templates/${template.id}`, payload, { withCredentials: true });
        toast.success('Mall uppdaterad');
      } else {
        await axios.post(`${API}/work-templates`, payload, { withCredentials: true });
        toast.success('Mall skapad');
      }
      onSave();
    } catch { toast.error('Kunde inte spara'); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="template-editor">
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Mallnamn t.ex. Udda-vecka standard"
        className="neo-input w-full"
        autoFocus
        data-testid="template-name"
      />
      <div className="space-y-1.5">
        {[0, 1, 2, 3, 4, 5, 6].map((wd) => {
          const day = days[wd];
          return (
            <div key={wd} className={`flex items-center gap-2 p-2 rounded-md ${day ? 'bg-[#FFF7E6]' : 'bg-gray-50'}`}>
              <span className="text-xs font-bold w-10 shrink-0">{WEEKDAYS_SV[wd]}</span>
              {day ? (
                <>
                  <select
                    value={day.workplace_id || ''}
                    onChange={(e) => setDay(wd, { workplace_id: e.target.value })}
                    className="neo-input text-xs flex-1 min-w-0"
                    data-testid={`template-day-${wd}-wp`}
                  >
                    <option value="">– välj –</option>
                    {workplaces.map((wp) => <option key={wp.id} value={wp.id}>{wp.name}</option>)}
                  </select>
                  <input
                    type="time"
                    value={day.start || ''}
                    onChange={(e) => setDay(wd, { start: e.target.value })}
                    className="neo-input text-xs w-[90px]"
                    data-testid={`template-day-${wd}-start`}
                  />
                  <input
                    type="time"
                    value={day.end || ''}
                    onChange={(e) => setDay(wd, { end: e.target.value })}
                    className="neo-input text-xs w-[90px]"
                    data-testid={`template-day-${wd}-end`}
                  />
                  <button type="button" onClick={() => removeDay(wd)} className="opacity-50 hover:opacity-100 p-1" data-testid={`template-day-${wd}-remove`}>
                    <Trash2 className="w-3.5 h-3.5 text-red-600" />
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setDay(wd, { workplace_id: workplaces[0]?.id || '', start: workplaces[0]?.default_start || '07:30', end: workplaces[0]?.default_end || '16:00' })}
                  className="text-xs text-[#D06892] font-bold hover:underline flex items-center gap-1"
                  data-testid={`template-day-${wd}-add`}
                >
                  <Briefcase className="w-3 h-3" /> Lägg till arbetsdag
                </button>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex gap-2">
        <button type="button" onClick={onCancel} className="neo-btn px-3 py-2 bg-gray-200 text-sm">Avbryt</button>
        <button type="submit" disabled={busy} className="neo-btn flex-1 py-2 bg-[#B9FBC0] font-bold" data-testid="template-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Spara mall'}
        </button>
      </div>
    </form>
  );
}
